import type {
  DomainEventPublisher,
  GermanLegalInfo,
  InvoiceData,
  InvoiceFinancials,
  InvoiceNumberValidator,
  PDFRenderer,
  ShippingCostPolicy,
  ShippingRateProvider,
} from "@invoicing-sdk/domain";
import {
  Invoice,
  InvoiceDraftNotFoundError,
  InvoiceStateViolationError,
  InvoiceStatus,
  OrderNotFoundError,
  ShippingCost,
} from "@invoicing-sdk/domain";
import type { InvoiceRepository, OrderRepository } from "../ports/index.js";
import type { CreateInvoiceCommand, CreateInvoiceResult } from "./commands.js";

/**
 * Dependencies required for CreateInvoice use case
 */
export interface CreateInvoiceDependencies {
  readonly orderRepository: OrderRepository;
  readonly invoiceRepository: InvoiceRepository;
  readonly invoiceNumberValidator: InvoiceNumberValidator;
  readonly pdfRenderer: PDFRenderer;
  readonly legalInfo: GermanLegalInfo;
  readonly shippingRateProvider: ShippingRateProvider;
  readonly shippingCostPolicy: ShippingCostPolicy;
  /**
   * Event publisher to publish {@link InvoiceCreated} events to.
   *
   * Can be omitted to not use event publishing.
   */
  readonly eventPublisher?: DomainEventPublisher;
}

/**
 * Use case for creating and finalizing an invoice
 * This creates the invoice with validation, PDF generation, and persistence
 *
 * Requirements:
 * - 1.1: Ensure the draft exists
 * - 1.2: Snapshot customer information and order items
 * - 1.3: Validate invoice number format
 * - 1.4: Calculate totals correctly
 * - 1.5: Generate PDF/A-3 compliant PDF
 * - 1.6: Make invoice immutable after creation
 *
 * @deprecated Use {@link makeIssueInvoice} instead.
 * The new API behaves the same, but has a more aligned naming convention.
 * This use case factory will be removed in the next major version.
 */
export function makeCreateInvoice(deps: CreateInvoiceDependencies) {
  return async (
    command: CreateInvoiceCommand
  ): Promise<CreateInvoiceResult> => {
    const {
      orderRepository,
      invoiceRepository,
      invoiceNumberValidator,
      shippingRateProvider,
      shippingCostPolicy,
      pdfRenderer,
      legalInfo,
    } = deps;
    const { orderId, invoiceNumber, createdBy } = command;

    // Check if order exists
    const order = await orderRepository.findById(orderId);
    if (!order) {
      throw new OrderNotFoundError(orderId);
    }

    // Check if invoice already exists for this order and is a draft.
    const existingInvoice = await invoiceRepository.findDraft({
      by: "invoiceNumber",
      invoiceNumber,
    });
    if (!existingInvoice) {
      throw new InvoiceDraftNotFoundError(orderId);
    }
    if (!existingInvoice.requireStatus(InvoiceStatus.DRAFT)) {
      throw new InvoiceStateViolationError(
        orderId,
        InvoiceStatus.DRAFT,
        existingInvoice.getStatus()
      );
    }

    // Calculate grand total (business rule validation)
    const grandTotal = order.subtotal
      .add(order.taxes)
      .add(order.shippingCost)
      .subtract(order.discount);

    // Calculate shipping costs (has business rules)
    const shippingCost = ShippingCost.determine(
      grandTotal,
      shippingRateProvider,
      shippingCostPolicy
    );

    // Prepare invoice financials
    const financials: InvoiceFinancials = {
      subtotal: order.subtotal,
      taxes: order.taxes,
      shippingCost: shippingCost,
      discount: order.discount,
      grandTotal,
    };

    // Prepare invoice data for PDF generation
    const invoiceDataForPdf: InvoiceData = {
      invoiceId: "", // Will be set after invoice creation
      invoiceNumber,
      orderId,
      customer: order.customer,
      items: order.items,
      financials,
      createdOn: new Date(),
      createdBy,
      legalInfo,
    };

    // Generate PDF
    const pdfBytes = await pdfRenderer.render(invoiceDataForPdf);

    // Issue a finalized invoice (which validates, emits event, etc.)
    // The IssueInvoiceData expects a draft
    const invoice = await Invoice.issue(
      {
        orderId,
        invoiceNumber,
        customerSnapshot: existingInvoice.customerSnapshot,
        itemsSnapshot: existingInvoice.itemsSnapshot,
        financials,
        createdBy: createdBy,
        status: InvoiceStatus.DRAFT, // This is always the case after the check above
      },
      pdfBytes,
      { invoiceNumberValidator: invoiceNumberValidator }
    );

    // Persist invoice
    await invoiceRepository.save(invoice);

    // Publish the events
    if (deps.eventPublisher) {
      invoice.publishAllUncommitted(deps.eventPublisher);
    }

    // Return result
    const result: CreateInvoiceResult = {
      invoiceId: invoice.invoiceId,
      invoiceNumber: invoice.invoiceNumber,
      orderId: invoice.orderId,
      grandTotal: invoice.financials.grandTotal.toPlainObject(),
      createdOn: invoice.createdOn,
      createdBy: invoice.createdBy,
    };

    return result;
  };
}

/**
 * Factory function to create CreateInvoice use case with dependencies
 *
 * @deprecated Use {@link makeIssueInvoice} instead.
 * The new API behaves the same, but has a more aligned naming convention.
 * This use case factory will be removed in the next major version.
 */
export function createCreateInvoiceUseCase(
  deps: CreateInvoiceDependencies
): (command: CreateInvoiceCommand) => Promise<CreateInvoiceResult> {
  return makeCreateInvoice(deps);
}
